import React, { useState } from 'react';
import { triggerNotification, useGetApiRequests } from '../../../common/CommonExport';
import { ApiError } from '../types';

interface UseDeleteKpiProps {
    monthYear: string;
    onSuccess: () => void;
    reportingManagerId?: number | string;
}

export const useDeleteKpi = ({ monthYear, onSuccess, reportingManagerId }: UseDeleteKpiProps) => {
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [deleteMessage, setDeleteMessage] = useState("");

    const deleteEmployeeKpiApi = useGetApiRequests('deleteEmployeeKpi', 'DELETE');

    const handleOpenDeleteModal = (employeeIds: number | number[]) => {
        const ids = Array.isArray(employeeIds) ? employeeIds : [employeeIds];
        setSelectedEmployees(ids);
        setDeleteMessage(`Are you sure you want to delete KPI for ${ids.length > 1 ? 'selected employees' : 'this employee'}?`);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setSelectedEmployees([]);
        setDeleteMessage("");
    };

    const handleDeleteKpi = async () => {
        if (selectedEmployees.length === 0 || !reportingManagerId) return;
        
        setModalLoading(true);
        try {
            const queryParams = { 
                monthYear: monthYear
            };
            const pathParams = {
                id: reportingManagerId
            };
            
            const payload = selectedEmployees;

            const response = await deleteEmployeeKpiApi(payload, queryParams, pathParams);

            if (response && 'data' in response && response.data.status.success === 'Success') {
                triggerNotification('success', response.data.status.message || "KPI(s) deleted successfully!", "", "topRight");
                handleCloseDeleteModal();
                setSelectedRowKeys([]);
                onSuccess();
            } else {
                 const errorMsg = (response as ApiError)?.data?.status?.message || "Failed to delete KPI(s).";
                triggerNotification('error', errorMsg, "", "topRight");
            }
        } catch (error) {
            console.error("Delete KPI error", error);
            const err = error as ApiError;
            const errorMessage = err.response?.data?.status?.message || err.response?.data?.message || err.message || "An error occurred while deleting KPI(s).";
            triggerNotification('error', errorMessage, "", "topRight");
        } finally {
            setModalLoading(false);
        }
    };

    return {
        isDeleteModalOpen,
        deleteMessage,
        modalLoadingDelete: modalLoading,
        handleOpenDeleteModal,
        handleCloseDeleteModal,
        handleDeleteKpi,
        selectedRowKeys,
        setSelectedRowKeys
    };
};
